// Pure geometry reads over element data — no DOM, no Konva. Shared by the
// renderer (fit/stray detection), interactions (lasso, label anchors) and the
// mutators (nudge/scale/duplicate offsets).

import { visibleAtDay } from '../dates';

// Box-shaped elements: positioned by their centre (x,y) with width/height.
// Images behave like rectangles (rotation included).
export function isBoxType(type) { return type === 'ellipse' || type === 'rectangle' || type === 'image'; }

// Geometric center of an element (label anchor). Null if it has no geometry.
export function centerOf(el) {
  const d = el.data || {};
  if (isBoxType(el.type)) return { x: d.x, y: d.y };
  const pts = (d.coordinates || []).filter((c) => Array.isArray(c) && typeof c[0] === 'number');
  if (!pts.length) return null;
  return { x: pts.reduce((a, c) => a + c[0], 0) / pts.length, y: pts.reduce((a, c) => a + c[1], 0) / pts.length };
}

// Center of an element's bounding box (null if it has no geometry).
export function elementBox(el) {
  const d = el.data || {};
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  if (isBoxType(el.type)) {
    const hw = (d.width || 0) / 2; const hh = (d.height || d.width || 0) / 2;
    minX = d.x - hw; maxX = d.x + hw; minY = d.y - hh; maxY = d.y + hh;
  } else {
    for (const c of d.coordinates || []) {
      if (Array.isArray(c) && typeof c[0] === 'number') { minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]); minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]); }
    }
  }
  if (!isFinite(minX)) return null;
  return { id: el.id, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

// Axis-aligned bounding rect of one element ({minX,minY,maxX,maxY}, null if it
// has no geometry). Rectangle/image rotation is honoured (rotated corners).
export function elementRect(el) {
  const d = el.data || {};
  if ((el.type === 'rectangle' || el.type === 'image') && d.rotation) {
    const hw = (d.width || 0) / 2; const hh = (d.height || 0) / 2;
    const cos = Math.cos(d.rotation); const sin = Math.sin(d.rotation);
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    for (const [px, py] of [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]]) {
      const x = d.x + px * cos - py * sin;
      const y = d.y + px * sin + py * cos;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    return { minX, minY, maxX, maxY };
  }
  if (isBoxType(el.type)) {
    const hw = (d.width || 0) / 2; const hh = (d.height || d.width || 0) / 2;
    return { minX: d.x - hw, minY: d.y - hh, maxX: d.x + hw, maxY: d.y + hh };
  }
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const c of d.coordinates || []) {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]);
      minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]);
    }
  }
  if (!isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

// Does the element's bounding rect overlap the marquee box? (v1 selects by
// overlap — a large bed is picked up even when its centre is outside the box.)
export function elementInBox(el, x1, y1, x2, y2) {
  const r = elementRect(el);
  return !!r && r.minX <= x2 && r.maxX >= x1 && r.minY <= y2 && r.maxY >= y1;
}

export function contentBounds(elements) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const acc = (x, y) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); };
  for (const el of elements) {
    const d = el.data || {};
    if (isBoxType(el.type)) {
      const hw = (d.width || 0) / 2; const hh = (d.height || d.width || 0) / 2;
      acc(d.x - hw, d.y - hh); acc(d.x + hw, d.y + hh);
    } else {
      for (const c of d.coordinates || []) if (Array.isArray(c) && typeof c[0] === 'number') acc(c[0], c[1]);
    }
  }
  if (!isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

function quantile(sorted, q) {
  if (!sorted.length) return 0;
  const i = (sorted.length - 1) * q; const lo = Math.floor(i); const hi = Math.ceil(i);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
}

// Ids of elements whose center is a far outlier from the cluster — a single
// mis-placed object hundreds of metres away. Uses an IQR fence on the distance
// to the median center, plus an absolute floor so a normally-spread plan never
// trips it. Returns [] when there are too few elements to judge.
export function strayIds(elements) {
  const boxes = elements.map(elementBox).filter(Boolean);
  if (boxes.length < 5) return [];
  const cxs = boxes.map((b) => b.cx).sort((a, b) => a - b);
  const cys = boxes.map((b) => b.cy).sort((a, b) => a - b);
  const medx = quantile(cxs, 0.5); const medy = quantile(cys, 0.5);
  const dists = boxes.map((b) => Math.hypot(b.cx - medx, b.cy - medy));
  const sd = dists.slice().sort((a, b) => a - b);
  const fence = quantile(sd, 0.75) + 3 * (quantile(sd, 0.75) - quantile(sd, 0.25));
  // Core extent from the inliers only (dist within the fence) so the strays
  // themselves don't inflate the floor.
  const inX = []; const inY = [];
  boxes.forEach((b, i) => { if (dists[i] <= fence) { inX.push(b.cx); inY.push(b.cy); } });
  const ext = (arr) => (arr.length ? Math.max(...arr) - Math.min(...arr) : 0);
  const floor = 4 * Math.max(ext(inX), ext(inY), 100); // never flag tight/normal plans
  const out = [];
  boxes.forEach((b, i) => { if (dists[i] > fence && dists[i] > floor) out.push(b.id); });
  return out;
}

// Map every numeric (x,y) pair in a coordinates array through `fn` — anchors at
// [0,1] and curve control points at [3,4]/[5,6] (closing entries hold theirs at
// [2,3]/[4,5]). Type tokens are left untouched.
export function mapCoords(coordinates = [], fn) {
  return coordinates.map((c) => mapCoord(c, fn));
}

export function mapCoord(coord, fn) {
  if (!Array.isArray(coord)) return coord;
  const out = coord.slice();
  for (let i = 0; i < out.length - 1; i += 1) {
    if (typeof out[i] === 'number' && typeof out[i + 1] === 'number') {
      const [x, y] = fn(out[i], out[i + 1]);
      out[i] = x; out[i + 1] = y;
      i += 1;
    }
  }
  return out;
}

export function shiftCoords(coordinates = [], dx, dy) {
  return mapCoords(coordinates, (x, y) => [x + dx, y + dy]);
}

export function shiftCoord(coord, dx, dy) {
  return mapCoord(coord, (x, y) => [x + dx, y + dy]);
}

// Scale every numeric (x,y) pair in a coordinate entry about `origin`.
export function scaleCoord(coord, origin, f) {
  return mapCoord(coord, (x, y) => [origin.x + (x - origin.x) * f, origin.y + (y - origin.y) * f]);
}

// Move an element (in place) by dx/dy — position for rect/ellipse, every
// coordinate for poly/line. Used on freshly-cloned copies.
export function offsetElement(el, dx, dy) {
  const d = el.data || {};
  if (isBoxType(el.type)) { d.x = (d.x || 0) + dx; d.y = (d.y || 0) + dy; }
  else if (Array.isArray(d.coordinates)) { d.coordinates = shiftCoords(d.coordinates, dx, dy); }
}

// Elements currently drawn: not on a hidden layer, not hidden image elements,
// and inside the timeline day when the timeline is on. Framing (fit, strays,
// "jump to content") works on these, so hidden content can't pull the view.
export function visibleElements(state) {
  const layers = new Map((state.layers || []).map((layer) => [layer.id, layer]));
  return (state.elements || []).filter((el) => {
    const d = el.data || {};
    const layer = d.layer ? layers.get(d.layer) : null;
    return !(el.type === 'image' && d.visible === false)
      && !(layer && layer.visible === false)
      && !(state.timelineOn && !visibleAtDay(el, state.timelineDay));
  });
}

// True when the plan has visible geometry but the viewport (width × height
// screen px) overlaps none of it — the user panned/zoomed completely away.
export function contentOffscreen(state, width, height, margin = 8) {
  const v = state.view || {};
  const scale = v.scale || 1;
  const x0 = -(v.posX || 0) / scale; const y0 = -(v.posY || 0) / scale;
  const x1 = x0 + (width || 0) / scale; const y1 = y0 + (height || 0) / scale;
  if (!(x1 > x0) || !(y1 > y0)) return false;
  const m = margin / scale; // screen-px margin in plan units
  const elements = visibleElements(state);
  for (const el of elements) {
    const r = elementRect(el);
    if (r && !(r.minX > x1 + m || r.maxX < x0 - m || r.minY > y1 + m || r.maxY < y0 - m)) return false;
  }
  return elements.length > 0;
}
